'use client';

import React from 'react';
import styles from './ArchivedContests.module.scss';
import { Accordion, AccordionIcon } from '@/components/accordion/Accordion';
import { getYearFromDate } from '@/services/date.service';
import { AccountHeaderBackground } from '@/account/account-layout/account-header-background/AccountHeaderBackground';
import { useTranslation } from '@app/i18n/useTranslationClient';
import { ContestImage } from '@/components/contests/contest-image/ContestImage';
import { ARCHIVED_CONTESTS } from '@/services/imageSizes.service';
import classNames from 'classnames';
import { isAuthenticatedClient } from '@packages/authenticator';
import { IContests } from '@/components/contests/contests.interfaces';
import { ArchivedContestLabel } from '@/components/contests/archived-contest-label/ArchivedContestLabel';

interface IArchivedContests {
    archivalContests: IContests[];
    desktopHeaderPath: string;
    mobileHeaderPath: string;
}

export const ArchivedContests: React.FC<IArchivedContests> = ({
    archivalContests,
    mobileHeaderPath,
    desktopHeaderPath,
}) => {
    const { t } = useTranslation();
    const isUserLogged = isAuthenticatedClient();

    const accordionWrapperStyle = isUserLogged ? styles.accordionWrapper : styles.accordionWrapperNotLogged;

    if (!archivalContests) {
        return null;
    }

    return (
        <article>
            <AccountHeaderBackground desktopBackgroundPath={desktopHeaderPath} mobileBackgroundPath={mobileHeaderPath}>
                <div className={styles.contestHeader}>
                    <h2 className={styles.archivedContestsHeaderTitle}>
                        {t('translations.account.contests.archivedContests')}
                    </h2>
                </div>
            </AccountHeaderBackground>
            <section className={styles.contestSection}>
                {archivalContests.map((contest, index) => (
                    <Accordion
                        key={index}
                        iconType={AccordionIcon.CHEVRON}
                        title={`${getYearFromDate(contest.year)}` || ''}
                        classes={{
                            wrapper: accordionWrapperStyle,
                            title: styles.accordionTitle,
                            titleWrapper: styles.accordionTitleWrapper,
                            icon: styles.accordionIcon,
                            openedAccordion: styles.accordionTitleOpened,
                        }}
                        defaultOpen={index === 0}>
                        <div className={classNames(styles.container, styles.archivedContestsWrapper)}>
                            {contest.contests.map((item, nestedIndex) => (
                                <div key={nestedIndex} className={styles.archivedContestsContent}>
                                    <ContestImage
                                        slug={item.slug}
                                        classes={styles.archivedContestsImage}
                                        mobileImagePath={item.thumbnailMobilePath}
                                        desktopImagePath={item.thumbnailDesktopPath}
                                        imageAlt={item.name}
                                        imageDimensions={ARCHIVED_CONTESTS}
                                    />
                                    <div className={styles.contest}>
                                        <h5 className={styles.contestTitle}>{item.name}</h5>
                                        <ArchivedContestLabel isResolved={item.resolved} />
                                        {item.description && (
                                            <div
                                                className={styles.contestDescription}
                                                dangerouslySetInnerHTML={{ __html: item.description }}
                                            />
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Accordion>
                ))}
            </section>
        </article>
    );
};
